import React, { useState } from "react";
import {
  Button,
  IconButton,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Box,
  Switch,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const VALUE_TYPES = ["string", "number", "boolean"];

const AddConfigModal = ({ open, onClose, onSave }) => {
  const [form, setForm] = useState({
    type: "",
    valueType: "string",
    value: "",
    boolValue: false,
  });
  const [errors, setErrors] = useState({});

  const handleClose = () => {
    setForm({ type: "", valueType: "string", value: "", boolValue: false });
    setErrors({});
    onClose();
  };

  const handleValueTypeChange = (_, newType) => {
    if (!newType) return;
    setForm((prev) => ({
      ...prev,
      valueType: newType,
      value: "",
      boolValue: false,
    }));
    if (errors.value) setErrors((prev) => ({ ...prev, value: "" }));
  };

  const handleChange = (field, val) => {
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSave = () => {
    const newErrors = {};
    if (!form.type.trim()) newErrors.type = "Type is required";
    if (form.valueType !== "boolean" && !String(form.value).trim())
      newErrors.value = "Value is required";
    if (form.valueType === "number" && isNaN(Number(form.value)))
      newErrors.value = "Must be a valid number";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    let finalValue;
    if (form.valueType === "boolean") finalValue = form.boolValue;
    else if (form.valueType === "number") finalValue = Number(form.value);
    else finalValue = form.value.trim();

    onSave({ type: form.type.trim(), value: finalValue });
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          fontFamily: "poppins",
          fontSize: 18,
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        New Configuration
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Type */}
          <Box>
            <Typography
              sx={{
                fontFamily: "poppins",
                fontSize: 13,
                fontWeight: 500,
                mb: 0.75,
                color: "var(--text-tertiary)",
              }}
            >
              Type <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. FORECAST_EXECUTION_TIME"
              value={form.type}
              onChange={(e) => handleChange("type", e.target.value)}
              error={!!errors.type}
              helperText={errors.type}
              inputProps={{ style: { fontFamily: "poppins", fontSize: 13 } }}
            />
          </Box>

          {/* Value Type selector */}
          <Box>
            <Typography
              sx={{
                fontFamily: "poppins",
                fontSize: 13,
                fontWeight: 500,
                mb: 0.75,
                color: "var(--text-tertiary)",
              }}
            >
              Value Type
            </Typography>
            <ToggleButtonGroup
              value={form.valueType}
              exclusive
              onChange={handleValueTypeChange}
              size="small"
              sx={{ width: "100%" }}
            >
              {VALUE_TYPES.map((vt) => (
                <ToggleButton
                  key={vt}
                  value={vt}
                  sx={{
                    flex: 1,
                    fontFamily: "poppins",
                    fontSize: 12,
                    textTransform: "capitalize",
                    py: 0.6,
                  }}
                >
                  {vt}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Value */}
          <Box>
            <Typography
              sx={{
                fontFamily: "poppins",
                fontSize: 13,
                fontWeight: 500,
                mb: 0.75,
                color: "var(--text-tertiary)",
              }}
            >
              Value{" "}
              {form.valueType !== "boolean" && (
                <span style={{ color: "red" }}>*</span>
              )}
            </Typography>
            {form.valueType === "boolean" ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Switch
                  checked={form.boolValue}
                  onChange={(e) => handleChange("boolValue", e.target.checked)}
                  color="primary"
                />
                <Typography
                  sx={{
                    fontFamily: "poppins",
                    fontSize: 13,
                    color: "var(--text-secondary)",
                  }}
                >
                  {form.boolValue ? "True" : "False"}
                </Typography>
              </Box>
            ) : (
              <TextField
                fullWidth
                size="small"
                placeholder={
                  form.valueType === "number" ? "e.g. 120" : "e.g. some text"
                }
                type={form.valueType === "number" ? "number" : "text"}
                value={form.value}
                onChange={(e) => handleChange("value", e.target.value)}
                error={!!errors.value}
                helperText={errors.value}
                inputProps={{ style: { fontFamily: "poppins", fontSize: 13 } }}
              />
            )}
          </Box>
        </Box>
      </DialogContent>

      <Divider sx={{ mt: 2 }} />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            fontFamily: "poppins",
            fontSize: 13,
            textTransform: "none",
            borderColor: "var(--border-color)",
            color: "var(--text-secondary)",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            fontFamily: "poppins",
            fontSize: 13,
            textTransform: "none",
            backgroundColor: "#007DFF",
            "&:hover": { backgroundColor: "#0056CC" },
          }}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddConfigModal;
